import { redirect, replace, type RouteObject } from "react-router";
import type { Region, SessionStore } from "@kovernow/flows/api";
import { AppShell, PublicShell } from "./shell/AppShell";
import { AccountHub, SettingsPage } from "./screens/Account";
import { Home } from "./screens/Home";
import { ProfilePage } from "./screens/Profile";
import { Vault } from "./screens/Vault";
import { AddItem } from "./screens/add-item/AddItem";
import { EditItem } from "./screens/item-detail/EditItem";
import { ItemDetail } from "./screens/item-detail/ItemDetail";
import { ChoosePlan } from "./screens/choose-plan/ChoosePlan";
import { NotFoundPage, Placeholder } from "./screens/Placeholder";
import { PartnerCapture } from "./screens/PartnerCapture";
import { ResetPasswordScreen } from "./screens/ResetPasswordScreen";
import { SignInScreen } from "./screens/SignInScreen";
import { SignUpScreen } from "./screens/SignUpScreen";
import { VerifyIdentity } from "./screens/verify/VerifyIdentity";
import { VerifyIdentityHk } from "./screens/verify/VerifyIdentityHk";
import { safeReturnTo } from "./core/safe-path";
import type { SingpassReturnStore } from "./core/singpass-return";

/**
 * Routes (CAP-4). Placeholders exist for every phase 1 journey so later
 * stories can link to them first. Never a travel route. `/p/<partner>` is
 * partner capture (W-1); no other route may use the `/p/` prefix.
 */
export const PLACEHOLDER_ROUTES: { path: string; titleKey: string }[] = [
  { path: "vault/:itemId/pay/:quotationId", titleKey: "nav.pay" },
  { path: "orders/:id", titleKey: "nav.orderStatus" },
  { path: "policies", titleKey: "nav.policies" },
  { path: "policies/:policyId", titleKey: "nav.policyDetail" },
  { path: "policies/:policyId/extend", titleKey: "nav.extendCover" },
  { path: "claims", titleKey: "nav.claims" },
  { path: "claims/new", titleKey: "nav.fileClaim" },
  { path: "notifications", titleKey: "nav.notifications" },
  { path: "account/verify", titleKey: "nav.verifyIdentity" },
  { path: "account/payment-methods", titleKey: "nav.paymentMethods" },
  { path: "account/invoices", titleKey: "nav.invoices" },
  { path: "account/support", titleKey: "nav.support" },
];

/**
 * Routes a region builds for real: `account/verify` is SingPass in SG (W-7)
 * and ShuftiPro in HK (W-8); Choose plan (W-10) is server-priced in both.
 */
const REGION_ROUTES: Record<Region, string[]> = {
  sg: ["account/verify", "vault/:itemId/plan"],
  hk: ["account/verify", "vault/:itemId/plan"],
};

export function buildRoutes(
  session: SessionStore,
  region: Region,
  singpassReturn?: SingpassReturnStore,
): RouteObject[] {
  const requireAuth = ({ request }: { request: Request }) => {
    const url = new URL(request.url);
    // W-7: a SingPass token is stripped before the sign-in redirect, so
    // `returnTo` never carries it. (Page loads are already stripped in main.)
    const clean = singpassReturn?.capture(url) ?? null;
    if (!session.isAuthenticated()) {
      // Whoever signs in next may be someone else: never use this token.
      singpassReturn?.dropToken();
      const target = clean ?? url;
      return redirect(`/sign-in?returnTo=${encodeURIComponent(target.pathname + target.search)}`);
    }
    if (clean) return replace(clean.pathname + clean.search);
    return null;
  };
  const publicOnly = ({ request }: { request: Request }) => {
    if (!session.isAuthenticated()) return null;
    return redirect(safeReturnTo(new URL(request.url).searchParams.get("returnTo")));
  };

  return [
    {
      element: <PublicShell />,
      children: [
        { path: "sign-in", loader: publicOnly, element: <SignInScreen /> },
        { path: "sign-up", loader: publicOnly, element: <SignUpScreen /> },
        { path: "reset-password", loader: publicOnly, element: <ResetPasswordScreen /> },
        // A signed-in visit is sent home by the screen (code dropped).
        { path: "p/:partner", element: <PartnerCapture /> },
      ],
    },
    {
      element: <AppShell />,
      loader: requireAuth,
      children: [
        { index: true, element: <Home /> },
        { path: "vault", element: <Vault /> },
        { path: "vault/add", element: <AddItem /> },
        { path: "vault/:itemId", element: <ItemDetail /> },
        { path: "vault/:itemId/edit", element: <EditItem /> },
        { path: "vault/:itemId/plan", element: <ChoosePlan /> },
        { path: "account", element: <AccountHub /> },
        { path: "account/settings", element: <SettingsPage /> },
        { path: "account/profile", element: <ProfilePage /> },
        { path: "account/verify", element: region === "sg" ? <VerifyIdentity /> : <VerifyIdentityHk /> },
        ...PLACEHOLDER_ROUTES.filter(({ path }) => !REGION_ROUTES[region].includes(path)).map(({ path, titleKey }) => ({
          path,
          element: <Placeholder titleKey={titleKey} />,
        })),
        { path: "*", element: <NotFoundPage /> },
      ],
    },
  ];
}
